// Adapted from https://github.com/fractal-ly/controlled-form-hook
// MIT License
// Copyright (c) 2021 Fractally

import * as React from 'react';
import { Schema, validate } from 'tiny-validation';
import { useBeforeUnload } from './useBeforeUnload';
import { useSafeState } from './useSafeState';

type InputTarget = EventTarget & HTMLInputElement;

export interface CustomTarget {
  name: string;
  type: string;
  value: unknown;
}

type Target = InputTarget | CustomTarget;

export type SimulatedChangeEvent<T extends Target> = {
  target: T;
  persist: undefined;
};

export type ChangeEvent = SimulatedChangeEvent<CustomTarget>;

type FormState<T> = {
  values: T;
  errors: Record<string, string[]>;
  visited: Record<string, boolean>;
  hasErrors: boolean;
  ignoreVisited: boolean;
};

export type UseFormResult<T> = {
  handleFieldChange: (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | ChangeEvent
  ) => void;
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>,
    ...arg: unknown[]
  ) => Promise<unknown>;
  isSubmitting: boolean;
  isDisabled: boolean;
  setValues: (values: T | ((v: T) => T)) => void;
  schema: Schema;
  setErrors: (errors: Record<string, string[]>) => void;
  reset: (values?: T) => void;
  isFieldVisited: (fieldName: string) => boolean;
  showAllErrors: () => void;
  isFormDirty: boolean;
  setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  cancelUnloadPromptRef: React.MutableRefObject<() => void>;
} & FormState<T>;

enum Actions {
  VALUES = 'set_values',
  ERRORS = 'set_errors',
  CHANGE = 'handle_change',
  RESET = 'reset',
  IGNORE_VISITED = 'ignore_visited'
}

type Action<T> =
  | {
      type: Actions.VALUES;
      payload: T | ((p: T) => T);
    }
  | {
      type: Actions.ERRORS;
      payload: Record<string, readonly string[]>;
    }
  | {
      type: Actions.CHANGE;
      payload: Target;
    }
  | {
      type: Actions.RESET;
      payload: T;
    }
  | {
      type: Actions.IGNORE_VISITED;
    };

const copyErrors = (errors: Record<string, readonly string[]>) => {
  const newErrors: Record<string, string[]> = {};
  Object.keys(errors).forEach(key => {
    newErrors[key] = [...errors[key]];
  });
  return newErrors;
};

function reducer<T extends Record<string, unknown>>(
  state: FormState<T>,
  action: Action<T>
): FormState<T> {
  if (action.type === Actions.VALUES) {
    if (action.payload instanceof Function) {
      return { ...state, values: { ...action.payload(state.values) } };
    }
    return { ...state, values: { ...action.payload } };
  } else if (action.type === Actions.ERRORS) {
    return {
      ...state,
      errors: copyErrors(action.payload),
      hasErrors: Object.keys(action.payload).length !== 0
    };
  } else if (action.type === Actions.RESET) {
    return {
      visited: {},
      errors: {},
      values: { ...action.payload },
      hasErrors: false,
      ignoreVisited: false
    };
  } else if (action.type === Actions.CHANGE) {
    const { name } = action.payload;
    let newValue: boolean | unknown;
    if (action.payload.type === 'checkbox') {
      const payload = action.payload as InputTarget;
      newValue = payload.checked ? 'true' : 'false';
    } else {
      newValue = action.payload.value;
    }
    return {
      ...state,
      values: { ...state.values, [name]: newValue },
      visited: { ...state.visited, [name]: true }
    };
  } else if (action.type === Actions.IGNORE_VISITED) {
    return {
      ...state,
      ignoreVisited: true
    };
  } else {
    throw new Error('Unknown action type ${action.type}');
  }
}

const getFormState = <T extends Record<string, unknown>>(
  values: T | (() => T)
): FormState<T> => ({
  values: values instanceof Function ? values() : values,
  errors: {},
  visited: {},
  hasErrors: false,
  ignoreVisited: false
});

const changeValue = <T extends Record<string, unknown>>(
  dispatch: React.Dispatch<Action<T>>,
  target: Target
) => dispatch({ type: Actions.CHANGE, payload: target });

const initialize = <T extends Record<string, unknown>>(
  dispatch: React.Dispatch<Action<T>>,
  values: T
) => dispatch({ type: Actions.RESET, payload: values });

type UseFormProps<T, S> = {
  onSubmit: (formValues: T, ...args: unknown[]) => Promise<S>;
  schema: Schema;
  initialValues: T | (() => T);
  disabledOverride?: boolean;
  beforeUnload?: boolean;
};

const useForm = <T extends Record<string, unknown>, S = unknown>({
  onSubmit,
  schema, // MUST be stable already (see the useEffect below)
  initialValues,
  disabledOverride = false,
  beforeUnload = false
}: UseFormProps<T, S>): UseFormResult<T> => {
  const [defaultValues] = React.useState(initialValues);
  const [state, dispatch] = React.useReducer<React.Reducer<FormState<T>, Action<T>>>(
    reducer,
    getFormState(initialValues)
  );
  const [isSubmitting, setIsSubmitting] = useSafeState(false);
  const [isFormDirty, setIsFormDirty] = useSafeState(false);

  const cancelUnloadPromptRef = useBeforeUnload(
    React.useCallback(() => beforeUnload && isFormDirty, [isFormDirty, beforeUnload])
  );

  const setValues = React.useCallback(
    (values: T | ((p: T) => T)) => dispatch({ type: Actions.VALUES, payload: values }),
    []
  );

  const setErrors = React.useCallback(
    (value: Record<string, readonly string[]>) =>
      dispatch({ type: Actions.ERRORS, payload: value }),
    []
  );

  React.useEffect(() => {
    validate(schema, state.values).fold(setErrors, () => setErrors({}));
  }, [schema, setErrors, state.values]);

  const reset = React.useCallback(
    (values = defaultValues) => {
      setIsSubmitting(false);
      initialize(dispatch, values instanceof Function ? values() : values);
    },
    [defaultValues, setIsSubmitting]
  );

  const handleSubmit = async (
    event?: React.FormEvent<HTMLFormElement>,
    ...args: unknown[]
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsSubmitting(true);
    if (Object.keys(state.errors).length == 0) {
      return onSubmit(state.values, ...args).finally(() => {
        setIsSubmitting(false);
        setIsFormDirty(false);
      });
    } else {
      setIsSubmitting(false);
      setIsFormDirty(true);
      return null;
    }
  };

  const handleFieldChange = React.useCallback(
    (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | ChangeEvent
        | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      event.persist?.();
      changeValue(dispatch, event.target);
      setIsFormDirty(true);
    },
    [setIsFormDirty]
  );

  // Checks if a field has been visited.
  const isFieldVisited = (fieldName: string) => {
    return state.visited['override'] || state.visited[fieldName];
  };

  // Marks all fields visited, to show all errors on the form whether the user has entered input or not.
  const showAllErrors = () => dispatch({ type: Actions.IGNORE_VISITED });

  const isDisabled = isSubmitting || disabledOverride || state.hasErrors;

  return {
    handleFieldChange,
    handleSubmit,
    isSubmitting,
    ...state,
    schema,
    isDisabled,
    isFieldVisited,
    showAllErrors,
    setValues,
    setErrors,
    reset,
    isFormDirty,
    setIsFormDirty,
    cancelUnloadPromptRef
  };
};

const createChangeEvent = <T extends Target>(target: T): SimulatedChangeEvent<T> => ({
  persist: undefined,
  target
});

export { useForm, createChangeEvent };
